import { facilityWorkspacePath } from '../routes';

/**
 * F-MK-076: the shared review period the reader is working on, per facility, so Collections reopens on
 * it after a trip to Document Processing (or a reload without ?period=) instead of jumping to the
 * business-date period. A per-viewer convenience: it lives in this browser only, persists until the reader chooses another period, and the page works without it.
 */
const KEY = (facilityId: number) => `abf.collections.period.${facilityId}`;

export const rememberCollectionsPeriod = (facilityId: number, periodId: number) => {
  try {
    window.localStorage.setItem(KEY(facilityId), JSON.stringify({ periodId, at: Date.now() }));
  } catch { /* storage unavailable: the page falls back to the server's period */ }
};

export const rememberedCollectionsPeriod = (facilityId: number): number | null => {
  try {
    const raw = window.localStorage.getItem(KEY(facilityId));
    if (!raw) return null;
    const { periodId } = JSON.parse(raw) as { periodId?: unknown; at?: unknown };
    const id = Number(periodId);
    if (!Number.isInteger(id) || id <= 0) return null;
    return id;
  } catch {
    return null;
  }
};

const positiveId = (value: unknown): number | null => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * The facility and collection period an imported paying-agent statement or servicer report was
 * recorded on, when the booking result names them (the agent's statement row carries facility_id
 * and period_id; the servicer report carries facilityId and servicerReport.periodId).
 */
export const landedCollectionsPeriod = (result: Record<string, any> | null | undefined): { facilityId: number; periodId: number; path: string } | null => {
  if (!result) return null;
  const nested = (result.servicerReport && typeof result.servicerReport === 'object' ? result.servicerReport : {}) as Record<string, unknown>;
  const facilityId = positiveId(result.facility_id ?? result.facilityId ?? nested.facility_id ?? nested.facilityId);
  const periodId = positiveId(result.period_id ?? result.periodId ?? nested.periodId ?? nested.period_id);
  if (!facilityId || !periodId) return null;
  return { facilityId, periodId, path: `${facilityWorkspacePath(facilityId, 'collections')}?period=${periodId}` };
};
